import { useState } from 'react';
import Modal from './Modal';
import { HelpCircle } from 'lucide-react';

const TRAIT_OPTIONS = {
  classCounter: [
    { value: 'retiarius', label: 'Specializes vs Retiarius' },
    { value: 'dimachaerus', label: 'Specializes vs Dimachaerus' },
    { value: 'provocator', label: 'Specializes vs Provocator' },
  ],
  primaryStat: [
    { value: 'strength', label: '+50 Strength' },
    { value: 'defense', label: '+50 Defense' },
    { value: 'technique', label: '+50 Technique' },
  ],
  secondaryStat: [
    { value: 'speed', label: '+50 Speed' },
    { value: 'stamina', label: '+50 Stamina' },
    { value: 'precision', label: '+50 Precision' },
  ],
  fightingStyle: [
    { value: 'aggressive', label: 'Aggressive Fighter' },
    { value: 'balanced', label: 'Balanced Fighter' },
    { value: 'defensive', label: 'Defensive Fighter' },
  ],
};

export default function TraitSelectionModal({ gladiator, onConfirm, onClose, required = false }) {
  const [classCounter, setClassCounter] = useState('');
  const [primaryStat, setPrimaryStat] = useState('');
  const [secondaryStat, setSecondaryStat] = useState('');
  const [fightingStyle, setFightingStyle] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const allSelected = classCounter && primaryStat && secondaryStat && fightingStyle;

  const handleContinue = () => {
    if (!allSelected) return;
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    onConfirm({
      class_counter: classCounter,
      primary_stat: primaryStat,
      secondary_stat: secondaryStat,
      fighting_style: fightingStyle,
    });
  };

  const getSelectedLabel = (category, value) => {
    const option = TRAIT_OPTIONS[category].find(opt => opt.value === value);
    return option ? option.label : '';
  };

  if (showConfirmation) {
    return (
      <Modal open={true} onClose={required ? undefined : () => setShowConfirmation(false)} maxWidth="md">
        <div className="p-6 space-y-6">
          <div>
            <h2 className="font-cinzel text-2xl text-amber-500 mb-2">Confirm Traits for {gladiator.name}</h2>
            <p className="text-sm text-red-400 flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              This is permanent and cannot be changed
            </p>
          </div>

          <div className="space-y-3 bg-zinc-900/40 border border-zinc-800 rounded p-4">
            <div className="flex items-center gap-2 text-zinc-200">
              <span className="text-emerald-400">✓</span>
              <span>{getSelectedLabel('classCounter', classCounter)}</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-200">
              <span className="text-emerald-400">✓</span>
              <span>{getSelectedLabel('primaryStat', primaryStat)}</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-200">
              <span className="text-emerald-400">✓</span>
              <span>{getSelectedLabel('secondaryStat', secondaryStat)}</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-200">
              <span className="text-emerald-400">✓</span>
              <span>{getSelectedLabel('fightingStyle', fightingStyle)}</span>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowConfirmation(false)}
              className="px-4 py-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-amber-600 text-zinc-900 font-semibold hover:bg-amber-500 rounded transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={true} onClose={required ? undefined : onClose} maxWidth="lg">
      <div className="p-6 space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-cinzel text-2xl text-amber-500">Choose Permanent Traits for {gladiator.name}</h2>
            <button
              onClick={() => setShowHelp(true)}
              className="flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 transition-colors"
              title="Learn about traits"
            >
              <HelpCircle size={16} className="text-orange-500" />
              <span className="underline decoration-dotted">What do traits do?</span>
            </button>
          </div>
          <p className="text-sm text-red-400 flex items-center gap-2 mt-2">
            <span className="text-lg">⚠️</span>
            These choices are permanent and cannot be changed
          </p>
        </div>

        {/* Class Specialization */}
        <div className="space-y-3">
          <h3 className="font-cinzel text-amber-400 text-sm uppercase tracking-wider">Class Specialization</h3>
          <div className="space-y-2">
            {TRAIT_OPTIONS.classCounter.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 p-3 border border-zinc-800 rounded cursor-pointer hover:bg-zinc-900/40 transition-colors"
              >
                <input
                  type="radio"
                  name="classCounter"
                  value={option.value}
                  checked={classCounter === option.value}
                  onChange={(e) => setClassCounter(e.target.value)}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="text-zinc-200">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Primary Stat Boost */}
        <div className="space-y-3">
          <h3 className="font-cinzel text-amber-400 text-sm uppercase tracking-wider">Primary Stat Boost</h3>
          <div className="space-y-2">
            {TRAIT_OPTIONS.primaryStat.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 p-3 border border-zinc-800 rounded cursor-pointer hover:bg-zinc-900/40 transition-colors"
              >
                <input
                  type="radio"
                  name="primaryStat"
                  value={option.value}
                  checked={primaryStat === option.value}
                  onChange={(e) => setPrimaryStat(e.target.value)}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="text-zinc-200">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Secondary Stat Boost */}
        <div className="space-y-3">
          <h3 className="font-cinzel text-amber-400 text-sm uppercase tracking-wider">Secondary Stat Boost</h3>
          <div className="space-y-2">
            {TRAIT_OPTIONS.secondaryStat.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 p-3 border border-zinc-800 rounded cursor-pointer hover:bg-zinc-900/40 transition-colors"
              >
                <input
                  type="radio"
                  name="secondaryStat"
                  value={option.value}
                  checked={secondaryStat === option.value}
                  onChange={(e) => setSecondaryStat(e.target.value)}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="text-zinc-200">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Fighting Style */}
        <div className="space-y-3">
          <h3 className="font-cinzel text-amber-400 text-sm uppercase tracking-wider">Fighting Style</h3>
          <div className="space-y-2">
            {TRAIT_OPTIONS.fightingStyle.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 p-3 border border-zinc-800 rounded cursor-pointer hover:bg-zinc-900/40 transition-colors"
              >
                <input
                  type="radio"
                  name="fightingStyle"
                  value={option.value}
                  checked={fightingStyle === option.value}
                  onChange={(e) => setFightingStyle(e.target.value)}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="text-zinc-200">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-zinc-800">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!allSelected}
            className="px-4 py-2 bg-amber-600 text-zinc-900 font-semibold hover:bg-amber-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <Modal open={true} onClose={() => setShowHelp(false)} maxWidth="md">
          <div className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle size={22} className="text-orange-500" />
                <h2 className="font-cinzel text-xl text-amber-500">What Do Traits Do?</h2>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Traits are permanent choices that shape your gladiator's combat style and provide lasting bonuses.
              </p>
            </div>

            <div className="space-y-4">
              {/* Primary & Secondary Stats */}
              <div className="bg-zinc-900/60 border border-amber-800/40 rounded-sm p-4">
                <h3 className="font-cinzel text-amber-400 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                  <span className="text-emerald-400">✓</span> Stat Boosts
                </h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  <strong className="text-amber-300">Primary</strong> and <strong className="text-amber-300">Secondary Stat</strong> choices immediately grant <strong className="text-emerald-400">+50</strong> to those skills. These bonuses are permanent and stack with training gains.
                </p>
              </div>

              {/* Class Specialization */}
              <div className="bg-zinc-900/60 border border-blue-800/40 rounded-sm p-4">
                <h3 className="font-cinzel text-blue-400 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                  <span className="text-blue-400">⚔️</span> Class Specialization
                </h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Your gladiator gains a <strong className="text-blue-400">small battle performance bonus</strong> when fighting against the chosen class. This boost applies to attack, defense, speed, and HP during combat vs that specific opponent type.
                </p>
              </div>

              {/* Fighting Style */}
              <div className="bg-zinc-900/60 border border-violet-800/40 rounded-sm p-4">
                <h3 className="font-cinzel text-violet-400 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                  <span className="text-violet-400">🎯</span> Fighting Style
                </h3>
                <p className="text-sm text-zinc-300 leading-relaxed mb-2">
                  You can still use <strong className="text-zinc-200">all three tactics</strong> (Aggressive, Balanced, Defensive) in every fight.
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  However, when you choose your gladiator's <strong className="text-violet-400">preferred fighting style tactic</strong>, they receive a <strong className="text-violet-400">small battle performance boost</strong> for that match. Use their strength at the right moment!
                </p>
              </div>
            </div>

            <div className="pt-3 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 italic">
                💡 Tip: Choose traits that complement your gladiator's natural strengths and your preferred battle strategy.
              </p>
            </div>

            <button
              onClick={() => setShowHelp(false)}
              className="w-full px-4 py-2 bg-amber-600 text-zinc-900 font-semibold hover:bg-amber-500 rounded transition-colors"
            >
              Got It
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
